import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/roles';

const BarChart = ({ data, color = '#38bdf8' }) => {
  if (!data || data.length === 0) return <p style={{ color: '#64748b', textAlign: 'center' }}>Sin datos</p>;

  const maxValue = Math.max(...data.map(d => d.total || d.total_revenue || 0));

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200, padding: '10px 0' }}>
      {data.map((item, i) => {
        const value = item.total || item.total_revenue || 0;
        const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: '100%',
              height: `${height}%`,
              background: `linear-gradient(to top, ${color}, ${color}80)`,
              borderRadius: '4px 4px 0 0',
              minHeight: 4,
              transition: 'height 0.5s ease-out',
            }} />
            <span style={{ color: '#64748b', fontSize: 10, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {item.label || item.name}
            </span>
            <span style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600 }}>
              {formatCurrency(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ReportsPage = () => {
  const [period, setPeriod] = useState('week');
  const [salesData, setSalesData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [salesRes, topRes, summaryRes] = await Promise.all([
        api.get(`/reports/sales?period=${period}`),
        api.get('/reports/top-products?limit=8'),
        api.get('/reports/summary'),
      ]);
      setSalesData(salesRes.data.data || []);
      setTopProducts(topRes.data.data || []);
      setSummary(summaryRes.data.data);
    } catch (err) {
      console.error('Error cargando reportes:', err);
      const backendMsg = err.response?.data?.message;
      setError(backendMsg ? `Error del servidor: ${backendMsg}` : 'No se pudieron cargar los reportes. Verifica la conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>Cargando reportes...</p>;

  if (error) return (
    <div>
      <h1 style={{ color: '#e2e8f0', marginBottom: 24, fontSize: 28 }}>📊 Reportes y Análisis</h1>
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>
        <button onClick={fetchData} className="btn btn-primary">Reintentar</button>
      </div>
    </div>
  );

  return (
    <div>
      <h1 style={{ color: '#e2e8f0', marginBottom: 24, fontSize: 28 }}>📊 Reportes y Análisis</h1>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Ventas Hoy', value: formatCurrency(summary.today_sales), icon: '💰', color: '#10b981' },
            { label: 'Ventas Semana', value: formatCurrency(summary.week_sales), icon: '📈', color: '#38bdf8' },
            { label: 'Ventas Mes', value: formatCurrency(summary.month_sales), icon: '📊', color: '#a855f7' },
            { label: 'Ticket Promedio', value: formatCurrency(summary.avg_ticket), icon: '🎫', color: '#f59e0b' },
            { label: 'Pedidos Hoy', value: summary.today_orders, icon: '📋', color: '#f472b6' },
          ].map((card, i) => (
            <div key={i} className="card" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{card.icon}</div>
              <p style={{ color: card.color, fontSize: 22, fontWeight: 700 }}>{card.value}</p>
              <p style={{ color: '#94a3b8', fontSize: 12 }}>{card.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Sales Chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#e2e8f0' }}>📈 Ventas por Período</h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {['day', 'week', 'month'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: 'none',
                  background: period === p ? '#38bdf8' : '#0f172a',
                  color: period === p ? '#0f172a' : '#94a3b8',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {p === 'day' ? 'Hoy' : p === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>
        </div>
        <BarChart data={salesData} color="#38bdf8" />
      </div>

      {/* Top Products */}
      <div className="card">
        <h3 style={{ color: '#e2e8f0', marginBottom: 16 }}>🏆 Productos Más Vendidos</h3>
        <BarChart data={topProducts} color="#fbbf24" />
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {topProducts.slice(0, 5).map((product, i) => (
            <div key={product.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px', background: '#0f172a', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : '#334155',
                  color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>{i + 1}</span>
                <span style={{ color: '#e2e8f0', fontSize: 14 }}>{product.name}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14 }}>{product.total_sold} vendidos</span>
                <span style={{ color: '#64748b', fontSize: 12, marginLeft: 10 }}>{formatCurrency(product.total_revenue)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;